"use client"

import { useConsentManager, useHeadlessConsentUI, type AllConsentNames } from "@c15t/nextjs"
import { useEffect, useState } from "react"
import { PRIVACY_POLICY_URL } from "./config"
import { CategoryRow } from "./category-row"
import { Button } from "./ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog"

export { CookieSettingsLink, type CookieSettingsLinkProps } from "./cookie-settings-link"

export interface CookieConsentDescriptions {
  banner?: string
  preferences?: string
  categories?: Partial<Record<AllConsentNames, string>>
}

export interface CategoryDefinition {
  name: AllConsentNames
  label: string
  description: string
  required?: boolean
}

const CATEGORIES: CategoryDefinition[] = [
  {
    name: "necessary",
    label: "Strictly Necessary",
    description:
      "These technologies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block or alert you about these technologies, but some parts of the site will not then work. These technologies do not store any personal data.",
    required: true,
  },
  {
    name: "functionality",
    label: "Functional",
    description:
      "These technologies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages. If you do not allow these technologies then some or all of these services may not function properly.",
  },
  {
    name: "measurement",
    label: "Performance",
    description:
      "These technologies allow us to count visits and understand traffic sources so we can measure and improve the performance of our site. For example, they help us to know which pages are the most and least popular and see how visitors move around the site. If you do not allow these technologies, your activities on our website will not contribute to our website performance reporting.",
  },
  {
    name: "marketing",
    label: "Marketing",
    description:
      "These technologies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.",
  },
]

// Partial so we only track the categories this package actually renders
// (no `experience` row, so no `experience` state). c15t's store still
// knows about that category globally; we just don't surface it here.
export type LocalConsentState = Partial<Record<AllConsentNames, boolean>>

// All rendered categories default to on — users can opt specific ones out
// via the dialog toggles before saving. The c15t store is only mutated on
// an explicit save/accept/reject action, so displaying these as on does
// NOT mark the user as consented.
const DEFAULT_LOCAL_STATE: LocalConsentState = {
  necessary: true,
  functionality: true,
  measurement: true,
  marketing: true,
}

/**
 * Mounts both the initial consent banner and the preference-center dialog.
 * Place once per app, inside <ConsentProvider>. Visibility is driven by
 * c15t's headless store (`useHeadlessConsentUI`); we never call into
 * c15t's rendered UI components.
 *
 * Gated on a post-mount state so SSR renders nothing. c15t reads consent
 * from localStorage/cookies on the client, and `manager.hasConsented()`
 * returns a different value pre- vs post-hydration for returning users.
 * If the banner renders during SSR, its open/closed state and button
 * labels (`"Close"` vs `"Back"`) flip between server and first client
 * render, producing React error #418 (text hydration mismatch) that then
 * cascades into #310 inside Next.js's AppRouter recovery path.
 */
export function CookieBanner({
  privacyPolicyUrl = PRIVACY_POLICY_URL,
  descriptions,
}: {
  descriptions?: CookieConsentDescriptions
  privacyPolicyUrl?: string
} = {}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  if (!mounted) return null
  return (
    <>
      <InitialBannerDialog privacyPolicyUrl={privacyPolicyUrl} description={descriptions?.banner} />
      <PreferenceDialog descriptions={descriptions} />
    </>
  )
}

function InitialBannerDialog({
  privacyPolicyUrl,
  description,
}: {
  privacyPolicyUrl: string
  description?: string
}) {
  const ui = useHeadlessConsentUI()
  const manager = useConsentManager()
  // Stay visible for any user who hasn't yet made a choice, independent of
  // `activeUI` — this way opening the preference dialog doesn't hide the
  // sticky banner underneath.
  const open = !manager.hasConsented()

  return (
    <Dialog
      open={open}
      // Non-modal so the sticky banner doesn't dim the page or block
      // interaction. `onOpenChange` no-op still prevents the banner being
      // dismissed without an explicit action per GDPR.
      modal={false}
      onOpenChange={() => {
        /* intentionally blocked */
      }}
    >
      <DialogContent
        position="bottom"
        // Prevent Radix auto-focusing the first tabbable element on open —
        // otherwise a footer button paints a focus ring and reads as
        // "pre-selected".
        onOpenAutoFocus={event => event.preventDefault()}
        onEscapeKeyDown={event => event.preventDefault()}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        {/* Radix requires a DialogTitle for a11y; hide it visually on the
            sticky banner per design (no heading, just body copy). */}
        <DialogTitle className="sr-only">Cookie notice</DialogTitle>
        <DialogDescription className="text-center">
          {description ??
            "We use cookies and similar technologies for various purposes, including ensuring that you get the best experience on our website, to help us understand our marketing efforts, and to reach potential customers across the web. To learn more, visit our"}{" "}
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Privacy Policy
          </a>
          {"."}
        </DialogDescription>
        {/* Mobile (below sm): 2-col grid with a full-width Accept row so all
            three actions fit large phones without stacking into one tall
            column. sm+: single centred row. Accept All is last in the DOM so
            screen readers reach opt-out actions first, matching the visual
            order. */}
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center sm:justify-center">
          <Button variant="outline" onClick={() => ui.openDialog()}>
            Settings
          </Button>
          <Button variant="outline" onClick={() => void ui.performBannerAction("reject")}>
            Reject All
          </Button>
          <Button className="col-span-2" onClick={() => void ui.performBannerAction("accept")}>
            Accept All
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PreferenceDialog({ descriptions }: { descriptions?: CookieConsentDescriptions }) {
  const ui = useHeadlessConsentUI()
  const manager = useConsentManager()
  const open = ui.activeUI === "dialog"

  // Local toggle state, initialised to "all on" and synced from c15t only
  // when the user has already consented in a prior session. This lets users
  // flip toggles freely without prematurely marking them as consented (c15t's
  // `setConsent` auto-persists, which would be wrong on individual toggles).
  const [localState, setLocalState] = useState<LocalConsentState>(DEFAULT_LOCAL_STATE)
  // Only one category row is expanded at a time — clicking another row
  // collapses the previously open one.
  const [expandedCategory, setExpandedCategory] = useState<AllConsentNames | null>(null)

  useEffect(() => {
    if (!open) return
    setExpandedCategory(null)
    if (manager.hasConsented()) {
      setLocalState(prev => ({
        ...prev,
        functionality: manager.has("functionality"),
        measurement: manager.has("measurement"),
        marketing: manager.has("marketing"),
      }))
    } else {
      setLocalState(DEFAULT_LOCAL_STATE)
    }
    // Intentionally run only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Close the preference dialog regardless of consent status — the sticky
  // banner underneath handles the "must decide" surface on its own, so we
  // don't need to force it back via `openBanner`.
  const handleClose = () => ui.closeUI()

  const handleSave = () => {
    for (const category of CATEGORIES) {
      if (category.required) continue
      manager.setConsent(category.name, localState[category.name] ?? false)
    }
    void ui.saveCustomPreferences()
  }

  const closeLabel = manager.hasConsented() ? "Close" : "Back"

  return (
    <Dialog open={open} onOpenChange={next => (next ? undefined : handleClose())}>
      <DialogContent
        // Prevent Radix auto-focusing the first tabbable element on open so
        // no footer button shows a pre-selected focus ring.
        onOpenAutoFocus={event => event.preventDefault()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label={closeLabel}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
          <span className="sr-only">{closeLabel}</span>
        </button>

        <DialogHeader className="pr-8">
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            {descriptions?.preferences ??
              "Choose which categories you want to allow. You can change this any time via the Cookie preferences link."}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable category list — lets long category descriptions overflow
            inside the card instead of pushing the footer off-screen. */}
        <div className="-mx-2 flex-1 overflow-y-auto px-2">
          <div className="flex flex-col">
            {CATEGORIES.map(category => (
              <CategoryRow
                key={category.name}
                category={{
                  ...category,
                  description: descriptions?.categories?.[category.name] ?? category.description,
                }}
                localState={localState}
                setLocalState={setLocalState}
                isExpanded={expandedCategory === category.name}
                onToggleExpanded={() =>
                  setExpandedCategory(current => (current === category.name ? null : category.name))
                }
              />
            ))}
          </div>
        </div>

        {/* Plain div + inline style for justify-end — `sm:justify-end` may
            or may not be emitted depending on host app scans, so we align
            via flex-end directly to guarantee right-alignment. */}
        <div
          className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2"
          style={{ justifyContent: "flex-end" }}
        >
          <Button variant="outline" onClick={() => void ui.performDialogAction("reject")}>
            Reject All
          </Button>
          <Button onClick={handleSave}>Confirm My Settings</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
